import { trackLoading } from '../utils/loadingManager';

const CATEGORY_DISPLAY_MAP = {
  NOTICIA: 'NOTÍCIAS',
  NOTICIAS: 'NOTÍCIAS',
  'NOTÍCIA': 'NOTÍCIAS',
  'NOTÍCIAS': 'NOTÍCIAS',
  EVENTO: 'EVENTOS',
  EVENTOS: 'EVENTOS',
  TECNOLOGIA: 'TECNOLOGIA'
};

const CATEGORY_QUERY_MAP = {
  NOTICIA: 'NOTICIA',
  NOTICIAS: 'NOTICIA',
  'NOTÍCIA': 'NOTICIA',
  'NOTÍCIAS': 'NOTICIA',
  EVENTO: 'EVENTO',
  EVENTOS: 'EVENTO',
  TECNOLOGIA: 'TECNOLOGIA'
};

const normalizeKey = (value) => {
  if (value === undefined || value === null) {
    return '';
  }
  return value.toString().toUpperCase();
};

const resolveNewsSource = () => {
  const basePath = process.env.BASE_URL || '/';
  const normalizedBase = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
  return `${normalizedBase || ''}/data/news.json`;
};

const toDisplayCategory = (value) => {
  const normalized = normalizeKey(value);
  return CATEGORY_DISPLAY_MAP[normalized] || value || 'NOTÍCIAS';
};

const toApiCategory = (value) => {
  const normalized = normalizeKey(value);
  return CATEGORY_QUERY_MAP[normalized] || normalized || 'NOTICIA';
};

const formatDateLabel = (value) => {
  if (!value) {
    return '';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  const datePart = parsed.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });

  const timePart = parsed.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  });

  return `${datePart} às ${timePart}`;
};

const normalizeArticle = (article) => {
  if (!article) return article;

  const dateISO = article.date || article.published_at || article.created_at || null;
  const displayCategory = toDisplayCategory(article.category);

  return {
    ...article,
    category: displayCategory,
    categoryKey: toApiCategory(article.category || displayCategory),
    dateISO,
    date: formatDateLabel(dateISO)
  };
};

const normalizeCollection = (items = []) => {
  return items
    .map(normalizeArticle)
    .sort((a, b) => {
      const dateA = a.dateISO ? new Date(a.dateISO).getTime() : 0;
      const dateB = b.dateISO ? new Date(b.dateISO).getTime() : 0;
      return dateB - dateA;
    });
};

const NEWS_SOURCE_URL = resolveNewsSource();

let cachedRawNews = null;
let cachedAllNews = null;

const fetchNewsDataset = async (forceRefresh = false) => {
  if (!forceRefresh && cachedRawNews) {
    return cachedRawNews;
  }

  const cacheBuster = forceRefresh ? `?t=${Date.now()}` : '';
  const targetUrl = `${NEWS_SOURCE_URL}${cacheBuster}`;

  const payload = await trackLoading(async () => {
    const response = await fetch(targetUrl, {
      headers: {
        Accept: 'application/json'
      },
      cache: forceRefresh ? 'reload' : 'default'
    });

    if (!response.ok) {
      throw new Error(`Falha ao carregar ${targetUrl}. Status ${response.status}`);
    }

    return response.json();
  });

  const dataset = Array.isArray(payload?.news) ? payload.news : (payload || []);
  cachedRawNews = dataset;
  cachedAllNews = null;
  return dataset;
};

export const getAllNews = async (forceRefresh = false) => {
  if (!forceRefresh && cachedAllNews) {
    return cachedAllNews;
  }

  const data = await fetchNewsDataset(forceRefresh);
  cachedAllNews = normalizeCollection(data);
  return cachedAllNews;
};

export const getNewsByCategory = async (category) => {
  const apiCategory = toApiCategory(category);
  const allNews = await getAllNews();
  return allNews.filter(article => article.categoryKey === apiCategory);
};

export const getNewsById = async (id) => {
  const numericId = Number(id);
  const allNews = await getAllNews();
  return allNews.find(article => Number(article.id) === numericId) || null;
};

export const getRelatedNews = async (currentId, limit = 3) => {
  const numericId = Number(currentId);
  const allNews = await getAllNews();
  const currentArticle = allNews.find(article => Number(article.id) === numericId);

  const sameCategory = currentArticle
    ? allNews.filter(article => article.categoryKey === currentArticle.categoryKey && Number(article.id) !== numericId)
    : [];

  const fallbackPool = allNews.filter(article => Number(article.id) !== numericId);

  return [...sameCategory, ...fallbackPool]
    .filter((article, index, array) => array.findIndex(item => Number(item.id) === Number(article.id)) === index)
    .slice(0, limit);
};

export const getCarouselNews = async (limit = 5) => {
  const allNews = await getAllNews();
  const featured = allNews.filter(article => article.featured);
  if (featured.length > 0) {
    return featured.slice(0, limit);
  }
  return allNews.slice(0, limit);
};
